import { Link } from '@tanstack/react-router'
import { CircleCheck, Info, TriangleAlert } from 'lucide-react'

import { Spinner } from '@/components/ui/spinner'

import type { DuplicateField, DuplicateStatus } from '../use-duplicate-check'

const SUBJECT: Record<DuplicateField, string> = {
  ghanaCardNumber: 'Ghana Card number',
  phoneNumber: 'mobile number',
}

export interface DuplicateNoticeProps {
  field: DuplicateField
  status: DuplicateStatus
  existingCustomerId: string | null
}

/**
 * What the duplicate check found, under the field it was asked about.
 *
 * It never blocks typing and it never blocks the step. The Ghana Card number is unique across the
 * whole institution, so a match here almost always means the person already has a file at some
 * branch — but the operator is the one holding the card, and the server checks again on
 * submission against an index this browser cannot reproduce.
 */
export function DuplicateNotice({ field, status, existingCustomerId }: DuplicateNoticeProps) {
  if (status === 'idle') {
    return null
  }

  if (status === 'checking') {
    return (
      <p className="flex items-center gap-1.5 text-xs text-muted">
        <Spinner className="size-3" />
        Checking every branch for this {SUBJECT[field]}…
      </p>
    )
  }

  if (status === 'clear') {
    return (
      <p className="flex items-center gap-1.5 text-xs text-success-text">
        <CircleCheck className="size-3.5 shrink-0" aria-hidden />
        Not held by any other customer.
      </p>
    )
  }

  if (status === 'unavailable') {
    return (
      <p className="flex items-start gap-1.5 text-xs text-muted">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        The duplicate check could not be made just now. Carry on — the server checks again when the
        record is submitted.
      </p>
    )
  }

  return (
    <div
      className="flex items-start gap-2 rounded-control border border-warning-border bg-warning-subtle p-2"
      role="status"
    >
      <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning-text" aria-hidden />
      <div className="space-y-0.5 text-xs">
        <p className="font-semibold text-heading">
          This {SUBJECT[field]} is already held by another customer.
        </p>
        <p className="text-body">
          The same person cannot be onboarded twice. Check whether this is the file you want before
          creating a second one — it may sit at a branch you cannot open.
        </p>
        {existingCustomerId ? (
          <Link
            to="/customers/$customerId"
            params={{ customerId: existingCustomerId }}
            className="inline-flex font-medium text-brand-text underline underline-offset-4 hover:text-brand-text-hover"
          >
            Open the existing record
          </Link>
        ) : null}
      </div>
    </div>
  )
}
